import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { ApiError } from "../api/client";
import { checkUsernameAvailability, signup } from "../api/auth";

export function SignupPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<{
    state: "idle" | "checking" | "ok" | "taken";
    reason?: string;
  }>({ state: "idle" });
  const navigate = useNavigate();

  // Live availability check, debounced.
  useEffect(() => {
    if (!username || username.length < 3) {
      setAvailability({ state: "idle" });
      return;
    }
    setAvailability({ state: "checking" });
    const handle = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(username);
        setAvailability({
          state: result.available ? "ok" : "taken",
          reason: result.reason ?? undefined,
        });
      } catch {
        setAvailability({ state: "idle" });
      }
    }, 280);
    return () => clearTimeout(handle);
  }, [username]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signup({ username, email, password });
      navigate("/verify-email", { state: { email } });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Sign-up failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const handleChip =
    availability.state === "ok"
      ? { label: "✓ Available", className: "text-moss" }
      : availability.state === "taken"
        ? { label: "× " + (availability.reason ?? "Taken"), className: "text-plum" }
        : availability.state === "checking"
          ? { label: "checking…", className: "text-ink-mute" }
          : null;

  return (
    <AuthLayout
      left={
        <div className="rise d2 max-w-md">
          <p className="eyebrow text-paper/55">A specimen book of identities</p>
          <h2 className="mt-4 max-w-[14ch] text-5xl tracking-tight text-paper [font-variation-settings:'opsz'_96,'SOFT'_30,'WONK'_1,'wght'_400]">
            Build a card people <em className="not-italic text-terra italic">remember</em>.
          </h2>
          <p className="mt-5 max-w-[38ch] text-lg text-paper/70">
            Three sections. One link. No app to install for the person you're handing it to.
          </p>
        </div>
      }
      right={
        <>
          <p className="eyebrow eyebrow-accent rise">02. New</p>
          <h1 className="rise d1 mt-3 mb-7 text-5xl tracking-tight">Pick a handle.</h1>

          <form className="rise d2 space-y-5" onSubmit={onSubmit}>
            <div>
              <label htmlFor="handle" className="field-label">
                Username
              </label>
              <input
                id="handle"
                type="text"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                className="field-input"
                required
                minLength={3}
                maxLength={32}
              />
              <div className="mt-3 flex items-center gap-3 rounded border border-dashed border-rule bg-paper-deep px-4 py-3">
                <span className="font-mono text-xs text-ink-mute">varo.biz/</span>
                <span className="font-display text-lg tracking-tight text-terra [font-variation-settings:'opsz'_22,'wght'_500,'SOFT'_30,'WONK'_1]">
                  {username || "your-handle"}
                </span>
                {handleChip && (
                  <span
                    className={`ml-auto font-mono text-[10px] uppercase tracking-[0.18em] ${handleChip.className}`}
                  >
                    {handleChip.label}
                  </span>
                )}
              </div>
              <small className="field-help">
                Lowercase letters, numbers, hyphens. Reserved words (admin, api, login, …) aren't
                available.
              </small>
            </div>

            <div>
              <label htmlFor="email" className="field-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input"
                required
              />
              <small className="field-help">We'll send a verification link.</small>
            </div>

            <div>
              <label htmlFor="pw" className="field-label">
                Password
              </label>
              <input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input"
                required
                minLength={10}
                maxLength={128}
              />
              <small className="field-help">At least 10 characters. No common passwords.</small>
            </div>

            {error && (
              <p className="rounded border border-plum/30 bg-plum/[0.04] px-3 py-2 text-sm text-plum">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={submitting || availability.state === "taken"}
            >
              {submitting ? "Creating…" : "Create my card"}
            </button>

            <p className="text-xs text-ink-mute">
              By signing up, you agree to the{" "}
              <Link to="/terms" className="uline">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="uline">
                Privacy Notice
              </Link>
              .
            </p>
          </form>

          <p className="rise d3 mt-7 text-sm text-ink-mute">
            Have an account?{" "}
            <Link to="/login" className="uline">
              Sign in
            </Link>
            .
          </p>
        </>
      }
    />
  );
}
